/**
 * GlobalView — Visual Mode Service (§14.2, Phase 6)
 *
 * Manages CesiumJS Post-Processing stages for visual presets:
 * Standard (no post-processing), CRT, NVG, FLIR.
 *
 * Each mode applies a custom GLSL fragment shader via
 * Cesium.PostProcessStage to the entire scene.
 *
 * All hardcoded shader values are exposed as GLSL uniforms
 * so they can be tuned live from the Admin panel.
 */

import * as Cesium from 'cesium';
import type { VisualMode, CrtSettings, NvgSettings, FlirSettings, FilterSettings } from '../stores/globeStore';
import { DEFAULT_CRT, DEFAULT_NVG, DEFAULT_FLIR } from '../stores/globeStore';

// ─── Unique ID prefix for our custom stages ────────────────────

const STAGE_PREFIX = 'gv_visual_mode_';

// ─── GLSL Fragment Shaders ─────────────────────────────────────

/**
 * CRT — Retro cathode ray tube monitor (§14.2)
 * Effects: scanlines, barrel distortion, phosphor glow, vignette
 */
const CRT_FRAGMENT = /* glsl */ `
uniform sampler2D colorTexture;
uniform float u_scanlineIntensity;
uniform float u_scanlineFrequency;
uniform float u_distortion;
uniform float u_greenTint;
uniform float u_bloom;
uniform float u_vignette;
in vec2 v_textureCoordinates;

void main() {
    vec2 uv = v_textureCoordinates;

    // Barrel distortion (subtle CRT curvature)
    vec2 centered = uv - 0.5;
    float dist = dot(centered, centered);
    uv = uv + centered * dist * u_distortion;

    // Check if distorted UV is out of bounds
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
        out_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
        return;
    }

    vec4 color = texture(colorTexture, uv);

    // Scanlines — horizontal dark bands
    float scanline = sin(uv.y * u_scanlineFrequency) * u_scanlineIntensity;
    color.rgb -= scanline;

    // Slight vertical line pattern (RGB subpixel simulation)
    float vline = sin(uv.x * 1200.0) * 0.03;
    color.rgb -= vline;

    // Phosphor green tint
    color.rgb = mix(color.rgb, color.rgb * vec3(0.7, 1.0, 0.7), u_greenTint);

    // Bloom / glow (brighten slightly)
    color.rgb *= u_bloom;

    // Vignette (darken edges)
    float vignette = 1.0 - dist * u_vignette;
    vignette = clamp(vignette, 0.0, 1.0);
    vignette = smoothstep(0.0, 1.0, vignette);
    color.rgb *= vignette;

    out_FragColor = vec4(color.rgb, 1.0);
}
`;

/**
 * NVG — Night Vision Goggles (§14.2)
 * Effects: green monochrome, noise/grain, bloom, vignette circular
 */
const NVG_FRAGMENT = /* glsl */ `
uniform sampler2D colorTexture;
uniform float u_brightness;
uniform float u_noiseIntensity;
uniform float u_vignetteSize;
uniform float u_greenAmount;
uniform float u_scanlines;
in vec2 v_textureCoordinates;

// Simple pseudo-random for grain
float rand(vec2 co) {
    return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
    vec2 uv = v_textureCoordinates;
    vec4 color = texture(colorTexture, uv);

    // Convert to luminance
    float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));

    // Boost brightness (NVG amplifies light)
    lum = pow(lum, 0.7) * u_brightness;
    lum = clamp(lum, 0.0, 1.0);

    // Green monochrome tint
    vec3 nvgColor = vec3(lum * 0.2, lum * u_greenAmount, lum * 0.15);

    // Film grain noise
    float noise = rand(uv + fract(czm_frameNumber * 0.01)) * u_noiseIntensity;
    nvgColor += noise * 0.5 - 0.03;

    // Circular vignette (NVG tube effect)
    vec2 centered = uv - 0.5;
    float dist = length(centered) * 2.0;
    float vignette = 1.0 - smoothstep(u_vignetteSize, 1.1, dist);
    nvgColor *= vignette;

    // Slight scanlines (very subtle)
    float scanline = sin(uv.y * 600.0) * u_scanlines;
    nvgColor -= scanline;

    out_FragColor = vec4(nvgColor, 1.0);
}
`;

/**
 * FLIR — Forward Looking Infrared / Thermal (§14.2)
 * Effects: grayscale → thermal color ramp (ironbow palette),
 * contrast enhancement
 */
const FLIR_FRAGMENT = /* glsl */ `
uniform sampler2D colorTexture;
uniform float u_contrast;
uniform float u_vignette;
in vec2 v_textureCoordinates;

// Ironbow thermal color palette
vec3 thermalColor(float t) {
    // Black → Dark Blue → Magenta → Orange → Yellow → White
    if (t < 0.2) {
        return mix(vec3(0.0, 0.0, 0.05), vec3(0.1, 0.0, 0.4), t / 0.2);
    } else if (t < 0.4) {
        return mix(vec3(0.1, 0.0, 0.4), vec3(0.7, 0.0, 0.5), (t - 0.2) / 0.2);
    } else if (t < 0.6) {
        return mix(vec3(0.7, 0.0, 0.5), vec3(1.0, 0.4, 0.0), (t - 0.4) / 0.2);
    } else if (t < 0.8) {
        return mix(vec3(1.0, 0.4, 0.0), vec3(1.0, 0.85, 0.0), (t - 0.6) / 0.2);
    } else {
        return mix(vec3(1.0, 0.85, 0.0), vec3(1.0, 1.0, 0.9), (t - 0.8) / 0.2);
    }
}

void main() {
    vec2 uv = v_textureCoordinates;
    vec4 color = texture(colorTexture, uv);

    // Compute luminance
    float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));

    // Contrast enhancement
    lum = (lum - 0.5) * u_contrast + 0.5;
    lum = clamp(lum, 0.0, 1.0);

    // Apply thermal color ramp
    vec3 thermal = thermalColor(lum);

    // Subtle vignette
    vec2 centered = uv - 0.5;
    float dist = dot(centered, centered);
    float vignette = 1.0 - dist * u_vignette;
    vignette = clamp(vignette, 0.0, 1.0);
    thermal *= vignette;

    out_FragColor = vec4(thermal, 1.0);
}
`;

// ─── Uniform Mappers ───────────────────────────────────────────

function crtUniforms(s: CrtSettings): Record<string, number> {
  return {
    u_scanlineIntensity: s.scanlineIntensity,
    u_scanlineFrequency: s.scanlineFrequency,
    u_distortion: s.distortion,
    u_greenTint: s.greenTint,
    u_bloom: s.bloom,
    u_vignette: s.vignette,
  };
}

function nvgUniforms(s: NvgSettings): Record<string, number> {
  return {
    u_brightness: s.brightness,
    u_noiseIntensity: s.noiseIntensity,
    u_vignetteSize: s.vignetteSize,
    u_greenAmount: s.greenAmount,
    u_scanlines: s.scanlines,
  };
}

function flirUniforms(s: FlirSettings): Record<string, number> {
  return {
    u_contrast: s.contrast,
    u_vignette: s.vignette,
  };
}

// ─── Stage Factory ─────────────────────────────────────────────

function createStage(
  name: string,
  fragmentShader: string,
  uniforms?: Record<string, number>,
): Cesium.PostProcessStage {
  return new Cesium.PostProcessStage({
    name: `${STAGE_PREFIX}${name}`,
    fragmentShader,
    uniforms: uniforms as Record<string, unknown> | undefined,
  });
}

// ─── Public API ────────────────────────────────────────────────

/** Remove all custom visual mode post-process stages */
export function clearVisualModes(viewer: Cesium.Viewer): void {
  if (!viewer || viewer.isDestroyed()) return;

  const stages = viewer.scene.postProcessStages;
  // Collect stages to remove (iterate backwards to avoid index issues)
  const toRemove: (Cesium.PostProcessStage | Cesium.PostProcessStageComposite)[] = [];
  for (let i = 0; i < stages.length; i++) {
    const stage = stages.get(i);
    if (stage?.name?.startsWith(STAGE_PREFIX)) {
      toRemove.push(stage);
    }
  }
  for (const stage of toRemove) {
    stages.remove(stage);
  }
}

/** Apply a visual mode to the CesiumJS viewer with optional tunable settings */
export function applyVisualMode(
  viewer: Cesium.Viewer,
  mode: VisualMode,
  settings?: FilterSettings,
): void {
  if (!viewer || viewer.isDestroyed()) return;

  // Always clear existing custom stages first
  clearVisualModes(viewer);

  // Standard mode = no post-processing
  if (mode === 'standard') return;

  const stages = viewer.scene.postProcessStages;

  switch (mode) {
    case 'crt':
      stages.add(createStage('crt', CRT_FRAGMENT, crtUniforms(settings?.crt ?? DEFAULT_CRT)));
      break;
    case 'nvg':
      stages.add(createStage('nvg', NVG_FRAGMENT, nvgUniforms(settings?.nvg ?? DEFAULT_NVG)));
      break;
    case 'flir':
      stages.add(createStage('flir', FLIR_FRAGMENT, flirUniforms(settings?.flir ?? DEFAULT_FLIR)));
      break;
  }
}
